import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CustomButton from 'components/Button/CustomButton'
import { useSnackbar } from 'hooks/useSnackbar'
import { useWallets } from 'providers/WalletsProvider'
import {
  buyPackWithWallet,
  getPackPurchaseInfo,
  InsufficientFundsError,
  PackPurchaseInfo
} from 'services/crossmint.service'
import { banxaCreateCryptoOrder } from 'services/banxa.api.service'
import SpinnerModal from 'components/Modal/SpinnerModal'
import BanxaModal from 'components/Modal/BanxaModal'

type CardState = 'loading' | 'ready' | 'buying' | 'topping-up'

interface UsdcPackPaymentCardProps {
  packPrice?: string
  packId?: string
  closePaymentModal: () => void
}

/**
 * The only way to buy a pack.
 *
 * Price and balance both come from `purchase-info`, which reads them from the
 * same chain state the purchase uses. When the balance falls short the Buy
 * button becomes Top Up, which opens Banxa at its standard top-up amount —
 * a single larger top-up costs less in flat fees than repeated pack-sized ones.
 */
function UsdcPackPaymentCard({
  packPrice,
  packId,
  closePaymentModal
}: UsdcPackPaymentCardProps) {
  const { openSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { refreshCrossmintBalance } = useWallets()

  const [cardState, setCardState] = useState<CardState>('loading')
  const [info, setInfo] = useState<PackPurchaseInfo | null>(null)
  const [isMinting, setIsMinting] = useState(false)
  const [banxaUrl, setBanxaUrl] = useState<string | null>(null)

  const loadInfo = useCallback(async () => {
    if (!packId) return
    try {
      setInfo(await getPackPurchaseInfo(parseInt(packId)))
    } catch (e: any) {
      openSnackbar(e?.message || 'Could not load pack price.')
    } finally {
      setCardState('ready')
    }
  }, [packId, openSnackbar])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (cancelled) return
      await loadInfo()
    })()
    return () => {
      cancelled = true
    }
  }, [loadInfo])

  // ── Buy ──────────────────────────────────────────────────────────────────
  const executeBuy = async () => {
    if (!packId) return
    setCardState('buying')
    setIsMinting(true)
    try {
      await buyPackWithWallet(parseInt(packId))

      // The transaction is already confirmed when this resolves — the backend
      // reads the token id out of the receipt — so there is nothing to poll.
      openSnackbar('Pack purchased successfully!')
      refreshCrossmintBalance()
      setIsMinting(false)
      closePaymentModal()
      navigate('/trophy-cabinet?section=packs', {
        state: { purchaseSuccess: true }
      })
    } catch (e: any) {
      setIsMinting(false)
      setCardState('ready')

      // Funds moved between page load and purchase — refresh so the card
      // switches to Top Up rather than showing a bare error.
      if (e instanceof InsufficientFundsError) {
        openSnackbar('Not enough USDC — top up to continue.')
        await loadInfo()
        return
      }
      openSnackbar(e?.message || 'Purchase failed. Please try again.')
    }
  }

  // ── Top up ───────────────────────────────────────────────────────────────
  const startTopUp = async () => {
    if (!info?.wallet) {
      openSnackbar('No wallet found on your account.')
      return
    }
    setCardState('topping-up')
    try {
      const { checkoutLink, iFrameCheckout } = await banxaCreateCryptoOrder({
        walletAddress: info.wallet,
        sourceCurrency: 'AUD',
        targetCurrency: 'USDC',
        returnUrlOnSuccess: `${window.location.origin}/packs`
      })
      const url = iFrameCheckout || checkoutLink
      if (!url) throw new Error('Banxa did not return a checkout link.')
      setBanxaUrl(url)
    } catch (e: any) {
      openSnackbar(e?.message || 'Could not start top up. Please try again.')
    } finally {
      setCardState('ready')
    }
  }

  const closeBanxa = async () => {
    setBanxaUrl(null)
    refreshCrossmintBalance()
    // Funds may not have landed yet; re-reading tells the buyer where they are.
    await loadInfo()
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const isBusy = cardState !== 'ready'
  const price = info?.priceUsdc ?? packPrice ?? '—'
  const balance = info ? Number(info.balanceUsdc).toFixed(2) : '...'
  const needsTopUp = info != null && !info.canAfford && !info.soldOut

  const label = (() => {
    if (cardState === 'loading') return 'Loading...'
    if (cardState === 'buying') return 'Buying...'
    if (cardState === 'topping-up') return 'Opening top up...'
    if (info?.soldOut) return 'Sold out'
    if (needsTopUp) return 'Top Up'
    return 'Buy with USDC Wallet'
  })()

  return (
    <div className="flex flex-col gap-2 justify-between p-4 w-full bg-card rounded-[3px]">
      {packId && (
        <div className="flex flex-col gap-2 w-full">
          <div className="flex gap-3 justify-between items-start w-full">
            <div className="flex flex-col gap-2 justify-center items-start w-full">
              <div className="flex justify-between items-center w-full font-bold">
                <div className="text-white">Pay with USDC Wallet</div>
                <p>{price} USDC</p>
              </div>
              <p className="text-xs text-grey3">
                Wallet balance: {balance} USDC
              </p>
              {needsTopUp ? (
                <p className="text-xs text-grey3">
                  You need {Number(info!.shortfallUsdc).toFixed(2)} USDC more to
                  buy this pack. Top up your wallet, then come back and buy.
                </p>
              ) : (
                <p className="text-xs">
                  Buy directly using your wallet balance
                </p>
              )}
            </div>
          </div>
          <CustomButton
            onClick={needsTopUp ? startTopUp : executeBuy}
            label={label}
            model="primary"
            variant="contained"
            disabled={isBusy || info?.soldOut}
          />
        </div>
      )}

      <SpinnerModal
        spinnerModalOpen={isMinting}
        setSpinnerModalOpen={setIsMinting}
        text="Minting your pack, please wait — you'll be redirected to your Trophy Cabinet shortly"
      />

      {banxaUrl && (
        <BanxaModal open={!!banxaUrl} onClose={closeBanxa} fullURL={banxaUrl} />
      )}
    </div>
  )
}

export default UsdcPackPaymentCard
